import { ActionDataTypes } from '../types'
import { ActivateButton } from '../components/Manager/ActivateButton'
import { Box, Spacer, Text, theme } from '@dtx-company/shared-components/src'
import { DeleteModeDrawer } from '../components/Manager/DeleteModeDrawer'
import { DraftJSEditor } from './RichTextEditor/DraftJSEditor'
import { FP_RICH_TEXT_BOX_SECTION } from '@dtx-company/true-common/src/constants/featureFlags'
import { FlowpageProps } from '../types/FlowpageProps'
import { InlineInput } from '@dtx-company/shared-components/src/components/atoms/InlineInput'
import { InlineManagerCard, ManagerProps } from '../components/Manager/Manager'
import { InlineTextBoxEditorProps } from './types'
import { LinkEditSection } from '../../../components/profile/Links/LinkEditSection'
import { LinkProvider } from '@dtx-company/inter-app/src/constants/linkTypes'
import { LockedTemplateField } from '../../FlowpageTemplates/LockedTemplateField/LockedTemplateField'
import { ManagerSettings } from '../components/Manager/ManagerSettings'
import { TEXTBOX_MAX } from '../validation'
import { Typography } from '@dtx-company/design-system/src'
import { clearPreviewLink } from '@dtx-company/inter-app/src/redux/slices/linkEditor/linkEditorSlice'
import { convertJSONtoEditorState } from './RichTextEditor/utils'
import { getFontSize, getSemanticHtmlElement } from './utils'
import { getLinkStyleValuesFromTheme } from '../../../components/flowpage/FlowpageLinkCard/utils/getLinkStyleValuesFromTheme'
import { sendSuccessNotification } from '@dtx-company/inter-app/src/utils/notifications'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useCurrentPagePermissions } from '../../../utils/useGetCurrentPagePermissions'
import { useDebouncedCallback } from 'use-debounce'
import { useDeleteLinkWithAutosave } from '../deleteUtils'
import { useDispatch } from 'react-redux'
import { useFlowFeature } from '@dtx-company/inter-app/src/hooks/useFlowFeature/useFlowFeature'
import { useFlowpageTheme } from '../../../components/profile/Customize/hooks'
import { useIsMobile } from '../../../utils/main'
import { useManagerSettings } from '../components/Manager/useManagerSettings'
import { useSubmitWidget } from '../submitUtils'
import { v4 as uuid } from 'uuid'
import OutsideClickHandler from 'react-outside-click-handler'
import React from 'react'
import events from '@dtx-company/inter-app/src/event-tracking/events/flowpage'
import messages from '@dtx-company/true-common/src/constants/messages'

const InlineTextBoxEditor: React.FC<InlineTextBoxEditorProps> = ({
  curr,
  focus,
  setFocus,
  dispatch,
  deleteWidget,
  mobileManager,
  charCount,
  setCharCount,
  disabled
}: InlineTextBoxEditorProps) => {
  // TODO: resolve this rule of hooks error - issue tracked in https://app.shortcut.com/flowcode/story/41455/resolve-rule-of-hooks-error-that-were-commented-out
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedCurr = React.useMemo(() => curr, [])
  const id = memoizedCurr ? memoizedCurr.id : uuid()
  const submitWidget = useSubmitWidget()

  const onInputChange = useDebouncedCallback(
    async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
      if (e.target.value.length > TEXTBOX_MAX) {
        e.target.value = e.target.value.substring(0, TEXTBOX_MAX)
      }
      if (setCharCount) setCharCount(e.target.value.length)

      const actionData: ActionDataTypes['textBoxSection'] = {
        textType: curr.actionData.textType,
        title: e.target.value
      }

      await submitWidget({
        curr: curr,
        actionData,
        handleClose: () => {
          dispatch(clearPreviewLink({}))
        },
        widgetType: 'textBoxSection',
        fields: {
          id,
          order: curr.order
        }
      })
    },
    500
  )

  // delete the widget if they finish editing it with no content
  const onBlurHandler = (value: string): void => {
    setFocus(false)
    if (value.length === 0) {
      deleteWidget()
    }
  }

  const fontSize = getFontSize(curr.linkTheme, curr.actionData)
  const colorFromThemeOrDefault =
    curr.linkTheme?.active && curr.linkTheme.fontColor
      ? curr.linkTheme.fontColor
      : 'flowpage.contrastText'
  const color = mobileManager ? colorFromThemeOrDefault : 'primary.black'
  return (
    <Box alignItems="center" width="100%" opacity={disabled ? '.35' : '1'}>
      <InlineInput
        disabled={disabled}
        type="text"
        defaultValue={curr.actionData.title || ''}
        placeholder={focus ? '' : `Enter ${getSemanticHtmlElement(curr.actionData)} text`}
        maxWidth={[null, null, '100%']}
        fontSize={fontSize}
        inputProps={{
          textAlign: 'center',
          color,
          border: mobileManager ? `1px dashed ${theme.colors.secondary.disabled}` : 'none',
          borderRadius: '4px'
        }}
        id={`${curr.id}-textbox-input`}
        borderRadius="4px"
        onChange={e => onInputChange(e)}
        onBlur={e => onBlurHandler(e.target.value)}
        onFocus={() => setFocus(true)}
      />
      {!mobileManager && focus && (
        <>
          <Spacer ml="8px" />
          <Text variant="body/small" color="primary.grey">{`${
            charCount ?? '0'
          }/${TEXTBOX_MAX}`}</Text>
        </>
      )}
    </Box>
  )
}

export const Flowpage: React.FC<FlowpageProps> = ({ link, editLinkMode }: FlowpageProps) => {
  const [focus, setFocus] = React.useState(false)
  const dispatch = useDispatch()
  const isMobile = useIsMobile()
  const { isViewer } = useAuthState()
  const { hasViewOnlyAccess } = useCurrentPagePermissions()
  const disabledInput = isViewer || hasViewOnlyAccess
  const deleteLink = useDeleteLinkWithAutosave()
  const [showRichText] = useFlowFeature(FP_RICH_TEXT_BOX_SECTION)
  const deleteWidget = (): void => {
    try {
      deleteLink({
        linkId: link.id,
        linkOrder: link.order
      })

      link.provider === LinkProvider.WIDGET
        ? events.userDeletedWidget({ link_id: link.id })
        : events.userDeletedLink({ link_id: link.id })
      sendSuccessNotification(messages.profile.textBox.delete)
    } catch (err) {
      console.error(err)
    }
  }
  const { theme: pageTheme } = useFlowpageTheme()
  if (isMobile && editLinkMode && !showRichText) {
    return (
      <InlineTextBoxEditor
        disabled={disabledInput}
        curr={link}
        focus={focus}
        setFocus={setFocus}
        dispatch={dispatch}
        deleteWidget={deleteWidget}
        mobileManager
      />
    )
  } else {
    const semanticTag = getSemanticHtmlElement(link.actionData)
    const fontSize = getFontSize(link.linkTheme, link.actionData)
    const { fontColor: color } = getLinkStyleValuesFromTheme(pageTheme, link.linkTheme)
    if (link?.actionData?.richText?.length && showRichText) {
      const editorState = convertJSONtoEditorState(link.actionData.richText)
      return (
        <Box width="100%" data-testid="rich-text-display-container" alignItems="center">
          <DraftJSEditor
            editorState={editorState}
            textAlignment={link.actionData.richTextAlignment}
            readOnly={true}
            containerProps={{
              color: color ? color : 'flowpage.contrastText',
              fontFamily: pageTheme?.fontFamily
            }}
          />
        </Box>
      )
    } else {
      return (
        <Box width="100%" justifyContent="center" alignItems="center">
          <Text
            as={semanticTag}
            lineHeight="unset"
            fontSize={fontSize}
            mr="4px"
            ml="4px"
            wordBreak="break-word"
            maxWidth="100%"
            fontWeight={semanticTag === 'h2' ? 'bold' : 'regular'}
            textAlign="center"
            color={color ? color : 'flowpage.contrastText'}
          >
            {link.actionData.title}
          </Text>
        </Box>
      )
    }
  }
}

export const Manager: React.FC<ManagerProps> = ({
  link,
  key,
  handle,
  editWidget
}: ManagerProps) => {
  const { isViewer } = useAuthState()
  const [focus, setFocus] = React.useState(false)
  const dispatch = useDispatch()
  const [charCount, setCharCount] = React.useState<number>(link.actionData?.title?.length ?? 0)
  const deleteLink = useDeleteLinkWithAutosave()
  const [showRichText] = useFlowFeature(FP_RICH_TEXT_BOX_SECTION)
  const {
    onDeleteLink,
    deleteLinkModeActive,
    toggleDeleteMode,
    setShowEditLink,
    showEditLink,
    toggleActivate,
    isActive,
    managerContainerStyles,
    hasViewOnlyAccess,
    toggleEditMode,
    isTemplateConfigLocked
  } = useManagerSettings(link, editWidget)

  const deleteWidget = (): void => {
    try {
      deleteLink({
        linkId: link.id,
        linkOrder: link.order
      })

      link.provider === LinkProvider.WIDGET
        ? events.userDeletedWidget({ link_id: link.id })
        : events.userDeletedLink({ link_id: link.id })
    } catch (err) {
      console.error(err)
    }
  }
  const memoizedCurr = React.useMemo(() => link, [link])
  const editorBorderStyle =
    focus && !showEditLink ? { border: `1px solid ${theme.colors.primary.flowBlue}` } : undefined
  const rightMargin = hasViewOnlyAccess ? '52px' : '12px'
  return (
    <InlineManagerCard
      key={key}
      {...editorBorderStyle}
      showEditLink={showEditLink}
      padding={'0'}
      {...managerContainerStyles.deactivatedContainerStyles}
      viewOnly={hasViewOnlyAccess || isTemplateConfigLocked}
    >
      {showEditLink && (
        <OutsideClickHandler display="contents" onOutsideClick={() => setShowEditLink(false)}>
          <Box alignItems="baseline" width="100%">
            <LinkEditSection handleClose={() => setShowEditLink(false)} />
          </Box>
        </OutsideClickHandler>
      )}
      {!showEditLink && (
        <Box width="100%" alignItems="center">
          {!isTemplateConfigLocked && (
            <ManagerSettings
              onClickEdit={toggleEditMode}
              onClickDelete={toggleDeleteMode}
              disabled={hasViewOnlyAccess}
              deleteModeActive={deleteLinkModeActive}
              viewOnly={hasViewOnlyAccess}
            />
          )}
          {isTemplateConfigLocked && <LockedTemplateField />}
          <Box ml="16px" mr="8px" width="100%">
            {showRichText ? (
              <Box width="100%" justifyContent="center">
                <Typography align="center">{memoizedCurr.actionData.title}</Typography>
              </Box>
            ) : (
              <InlineTextBoxEditor
                disabled={isViewer || hasViewOnlyAccess || isTemplateConfigLocked}
                curr={memoizedCurr}
                focus={focus}
                setFocus={setFocus}
                dispatch={dispatch}
                deleteWidget={deleteWidget}
                mobileManager={false}
                charCount={charCount}
                setCharCount={setCharCount}
              />
            )}
          </Box>
          <>
            <ActivateButton
              viewOnly={hasViewOnlyAccess || isTemplateConfigLocked}
              toggleActivated={toggleActivate}
              activated={isActive}
            />
            <Spacer mr="12px" />
            {!hasViewOnlyAccess && !isTemplateConfigLocked && handle}
            <Spacer mr={rightMargin} />
          </>
        </Box>
      )}
      {!showEditLink && (
        <DeleteModeDrawer
          deleteModeActive={deleteLinkModeActive}
          toggleDeleteMode={toggleDeleteMode}
          onDelete={onDeleteLink}
        />
      )}
    </InlineManagerCard>
  )
}
